// API Thẻ liệu trình V2 — /api/v2/treatment (GĐ1: gói liệu trình, quy tắc chung)
// Tài liệu: test.buso.asia/khothi/the_lieu_trinh/firebase_SO_DO_FLOW_API_THE_LIEU_TRINH.html
// Response chuẩn: { status, success, message, data } — lỗi có `code` (PACKAGE_...) và `errors` theo ô.

import { authFetch } from '../services/mixin';

/* ================= TYPES ================= */

export type PackageKind = 'session' | 'value';
export type PackageItemRole = 'main' | 'bonus';
export type PriceSource = 'promo' | 'branch' | 'list';
export type PromoState = 'running' | 'upcoming' | 'ended';

export type TreatmentErrorCode =
    | 'PACKAGE_CODE_REQUIRED' | 'PACKAGE_CODE_EXISTS' | 'PACKAGE_NAME_REQUIRED' | 'PACKAGE_MAIN_SERVICE_REQUIRED'
    | 'PACKAGE_DUPLICATE_SERVICE' | 'SERVICE_NOT_FOUND' | 'PACKAGE_PRICE_REQUIRED' | 'PACKAGE_FACE_VALUE_INVALID'
    | 'PACKAGE_PROMO_INVALID' | 'PACKAGE_REFUND_FEE_INVALID' | 'VALIDATION_ERROR' | 'PACKAGE_NOT_FOUND'
    | 'PACKAGE_NOT_SELLING' | 'DISCOUNT_APPROVAL_REQUIRED' | 'FORBIDDEN' | 'PACKAGE_KIND_LOCKED' | string;

export interface TreatmentResult<T> {
    ok: boolean;
    data: T | null;
    message: string;
    code?: TreatmentErrorCode;
    /** Lỗi theo ô: { code: ["Mã gói đã tồn tại"], "items.1.service_id": [...] } */
    errors?: Record<string, string[]>;
    /** Mã HTTP khi lỗi, để phân biệt thiếu quyền với lỗi máy chủ */
    status?: number;
}

export interface PackageItemDto {
    service_id: number;
    service_name?: string;
    service_price?: number;
    sessions: number;
    role: PackageItemRole;
}

export interface PackagePromo { price: number; from: string; to: string; label: string | null }

export interface CurrentPrice { price: number; base: number; source: PriceSource; promo_label: string | null }

export interface BranchPrice { branch_id: number; branch_name?: string; price: number | null }

export interface PackageListItem {
    id: number;
    code: string;
    name: string;
    kind: PackageKind;
    items: PackageItemDto[];
    price: number;
    face_value: number;
    validity_months: number;
    current_price: CurrentPrice;
    has_branch_prices: boolean;
    /** Chỉ có khi gọi include=branch_prices */
    branch_prices?: BranchPrice[];
    promo: PackagePromo | null;
    promo_state: PromoState | null;
    allow_refund: boolean;
    refund_fee_percent: number;
    sold_count: number;
    active_cards_count: number;
    is_active: boolean;
    updated_at: string;
}

export interface PackageList {
    items: PackageListItem[];
    summary: { active: number; total: number };
    pagination: { page: number; per_page: number; total: number };
}

export interface PackageDetail extends PackageListItem {
    preview_lines: { service_id: number; sessions: number; role: PackageItemRole; unit_value: number }[];
    list_value: number;
    bonus_value: number;
    savings_amount: number;
    savings_percent: number;
    branch_prices: BranchPrice[];
    note: string | null;
    created_at: string;
    /** Chỉ có ở kết quả PUT */
    history_added?: boolean;
}

export interface PackageBody {
    code: string;
    name: string;
    kind: PackageKind;
    items: { service_id: number; sessions: number; role: PackageItemRole }[];
    price: number;
    face_value?: number;
    validity_months: number;
    branch_prices: { branch_id: number; price: number }[];
    promo: PackagePromo | null;
    allow_refund: boolean;
    refund_fee_percent: number;
    is_active: boolean;
    note: string;
}

export interface PriceHistoryItem {
    id: number;
    changed_at: string;
    changed_by: number | null;
    changed_by_name: string | null;
    changes: string[];
}

export interface TreatmentConfigDto {
    branch_id: number | null;
    is_branch_override: boolean;
    low_sessions: number;
    low_value_percent: number;
    expiring_days: number;
    due_soon_days: number;
    block_overdue: boolean;
    limit_by_paid: boolean;
    cancel_usage_hours: number;
    refund_fee_percent: number;
    refund_approval_threshold: number;
}

export interface PackageQuote {
    package_id: number;
    price_today: number;
    base: number;
    source: PriceSource;
    promo_label: string | null;
    list_price: number;
    discount_cap_percent: number;
    discount_percent: number;
    discount_amount: number;
    final_price: number;
}

/* ---------- GĐ2a: thẻ liệu trình ---------- */

export type CardStatusDto = 'active' | 'expired' | 'completed' | 'refunded' | 'cancelled';
export type CardFlagKey = 'low' | 'expiring' | 'overdue' | 'due_soon' | 'debt';
export type InstallmentStateDto = 'paid' | 'overdue' | 'due_soon' | 'upcoming' | 'void';

export interface CardFlagsDto {
    low: boolean;
    expiring: boolean;
    days_to_expire: number;
    overdue: boolean;
    overdue_amount: number;
    max_overdue_days: number;
    due_soon: boolean;
    debt: boolean;
    pending_refund: boolean;
}

export interface CardListItem {
    card_id: number;
    card_code: string;
    customer: { id: number; name: string; phone: string | null } | null;
    package_id: number;
    package_code: string;
    package_name: string;
    kind: PackageKind;
    source: 'pos' | 'import' | string;
    status: CardStatusDto;
    /** Trạng thái hiển thị (đã tính hết hạn / dùng hết) */
    display_status: CardStatusDto;
    balance: {
        total_sessions?: number; used_sessions?: number; remaining_sessions?: number;
        face_value?: number; used_value?: number; remaining_value?: number;
        pct_left: number;
    };
    quantity: number;
    list_price: number;
    price: number;
    paid_amount: number;
    outstanding: number;
    consumed_amount: number;
    issued_at: string;
    expires_at: string;
    branch: { id: number; name: string } | null;
    sold_by_name: string | null;
    consultant: { id: number; name: string } | null;
    order: { id: number; order_code: string; payment_type: string; shared: boolean } | null;
    flags: CardFlagsDto;
}

export interface CardList {
    items: CardListItem[];
    tab_counts: Record<CardStatusDto, number>;
    pagination: { page: number; per_page: number; total: number };
}

export interface CardLineDto {
    line_id: number;
    service_id: number;
    service_name: string;
    service_group?: string | null;
    role: PackageItemRole;
    total_sessions: number;
    bonus_sessions: number;
    used: number;
    refunded: number;
    booked: number;
    available: number;
    unit_value: number;
}

export interface CardInstallmentDto {
    no: number;
    due_date: string;
    amount: number;
    paid: number;
    left: number;
    state: InstallmentStateDto;
    days_left: number;
    overdue_days: number;
}

export interface CardPaymentDto {
    id: number;
    payment_method: string;
    amount: number;
    transaction_code: string | null;
    cashier_name: string | null;
    paid_at: string;
}

export interface CardEventDto {
    event_id: number;
    kind: string;
    text: string;
    amount: number | null;
    created_by_name: string | null;
    created_at: string;
}

export interface CardDetailDto extends CardListItem {
    lines: CardLineDto[];
    installments: CardInstallmentDto[];
    payments: CardPaymentDto[];
    /** GĐ2b / GĐ3 — hiện luôn rỗng */
    usages: unknown[];
    bookings: unknown[];
    refunds: unknown[];
    extensions: unknown[];
    allowed_service_ids: number[];
    policy: { allow_refund: boolean; refund_fee_percent: number };
    events: CardEventDto[];
}

export interface CardListParams {
    search?: string;
    status?: CardStatusDto;
    flag?: CardFlagKey;
    package_id?: number;
    branch_id?: number;
    customer_id?: number;
    kind?: PackageKind;
    page?: number;
    per_page?: number;
}

export interface PackageListParams {
    search?: string;
    kind?: PackageKind;
    is_active?: boolean;
    branch_id?: number;
    include?: 'branch_prices';
    page?: number;
    per_page?: number;
}

/* ================= HELPERS ================= */

const BASE = '/api/v2/treatment';

const request = async <T,>(url: string, init: RequestInit = {}): Promise<TreatmentResult<T>> => {
    try {
        const res = await authFetch(url, {
            ...init,
            headers: { Accept: 'application/json', ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...init.headers },
        });
        let json: any = null;
        try {
            json = await res.json();
        } catch {
            return { ok: false, data: null, message: `Máy chủ trả về dữ liệu không hợp lệ (HTTP ${res.status})` };
        }
        const ok = res.ok && json?.success === true;
        return {
            ok,
            data: ok ? (json.data as T) : null,
            message: json?.message || (ok ? '' : `Lỗi máy chủ (HTTP ${res.status})`),
            ...(ok ? {} : { code: json?.code, errors: json?.errors, status: res.status }),
        };
    } catch (e: any) {
        return { ok: false, data: null, message: e?.message === 'Unauthorized' ? 'Phiên đăng nhập đã hết hạn' : 'Không kết nối được máy chủ' };
    }
};

const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') p.append(k, String(v));
    });
    const s = p.toString();
    return s ? `?${s}` : '';
};

const send = <T,>(method: string, url: string, body: unknown) => request<T>(url, { method, body: JSON.stringify(body) });

/* ================= API ================= */

/** 1. Danh sách gói (giá bán hôm nay theo chi nhánh; include=branch_prices cho chế độ bảng giá) */
export const getTreatmentPackages = (params: PackageListParams = {}) =>
    // Danh mục dùng ở cả Báo giá của Telesales: thiếu quyền đọc thì trả status 403 để màn đó báo lỗi
    request<PackageList>(`${BASE}/packages${qs({ ...params })}`);

/** 2. Chi tiết gói: giá trị/buổi, giá từng chi nhánh (chi nhánh chưa đặt giá: price null) */
export const getTreatmentPackage = (id: number) => request<PackageDetail>(`${BASE}/packages/${id}`);

/** 3. Thêm gói */
export const createTreatmentPackage = (body: PackageBody) => send<PackageDetail>('POST', `${BASE}/packages`, body);

/** 4. Sửa gói — gửi đủ trường; không gửi promo là xoá khuyến mãi */
export const updateTreatmentPackage = (id: number, body: PackageBody) => send<PackageDetail>('PUT', `${BASE}/packages/${id}`, body);

/** 5. Bật / tắt bán */
export const setTreatmentPackageActive = (id: number, isActive: boolean) =>
    send<{ id: number; is_active: boolean; updated_at: string }>('PATCH', `${BASE}/packages/${id}/active`, { is_active: isActive });

/** 6. Lịch sử giá, mới nhất trước */
export const getTreatmentPackagePriceHistory = (id: number) => request<PriceHistoryItem[]>(`${BASE}/packages/${id}/price-history`);

/** 7. Báo giá khi bán: giá hôm nay tại chi nhánh + chiết khấu (vượt hạn mức → DISCOUNT_APPROVAL_REQUIRED) */
export const quoteTreatmentPackage = (id: number, params: { branch_id?: number; discount_type?: 'percent' | 'amount'; discount_value?: number; approved_by?: number } = {}) =>
    request<PackageQuote>(`${BASE}/packages/${id}/quote${qs({ ...params })}`);

/** 12. Danh sách thẻ + số thẻ theo tab trạng thái */
export const getTreatmentCards = (params: CardListParams = {}) => request<CardList>(`${BASE}/cards${qs({ ...params })}`);

/** 13. Chi tiết thẻ theo id hoặc mã TLT-… */
export const getTreatmentCard = (idOrCode: number | string) => request<CardDetailDto>(`${BASE}/cards/${encodeURIComponent(String(idOrCode))}`);

/** 8. Xuất bảng giá (xlsx) — trả về file để tải */
export const exportTreatmentPackages = async (params: Omit<PackageListParams, 'page' | 'per_page' | 'include'> & { format?: 'xlsx' | 'csv' } = {}):
    Promise<{ ok: boolean; blob?: Blob; filename?: string; message: string }> => {
    try {
        const res = await authFetch(`${BASE}/packages/export${qs({ ...params })}`);
        if (!res.ok) {
            const json = await res.json().catch(() => null);
            return { ok: false, message: json?.message || `Không xuất được bảng giá (HTTP ${res.status})` };
        }
        const cd = res.headers.get('Content-Disposition') || '';
        const filename = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd)?.[1];
        return { ok: true, blob: await res.blob(), filename: filename ? decodeURIComponent(filename) : undefined, message: '' };
    } catch (e: any) {
        return { ok: false, message: e?.message === 'Unauthorized' ? 'Phiên đăng nhập đã hết hạn' : 'Không kết nối được máy chủ' };
    }
};

/** 9. Quy tắc chung (branch_id không gửi = cấu hình chung) */
export const getTreatmentConfig = (branchId?: number) => request<TreatmentConfigDto>(`${BASE}/config${qs({ branch_id: branchId })}`);

export const updateTreatmentConfig = (body: Partial<Omit<TreatmentConfigDto, 'is_branch_override'>> & { branch_id: number | null }) =>
    send<TreatmentConfigDto>('PUT', `${BASE}/config`, body);
